import translate from './translate';
import {sprintf} from 'sprintf-js';
import { fieldRules } from './validationFieldRules';

/**
 * Helper to get validator params.
 *
 * Tries multiple sources in order:
 * 1. Error entry passed directly from InputWrap (most reliable)
 * 2. Vuelidate 2 $silentErrors / $errors by validator name
 * 3. Vuelidate 2 $params (may be flat: { max: 5 } instead of nested)
 * 4. Vuelidate 1 $params (nested: { maxLength: { max: 5 } })
 * 5. Static $params from fieldRules validator functions (last resort for
 *    helpers.forEach items where the proxy doesn't expose $params)
 */
function getParams(v, validatorName, errorEntry) {
  // 1. Error entry passed directly (from InputWrap's failedValidations block)
  if (errorEntry?.$params) {
    return errorEntry.$params;
  }

  // 2. Vuelidate 2: $silentErrors / $errors by validator name
  const errors = v?.$silentErrors || v?.$errors || [];
  if (Array.isArray(errors)) {
    const error = errors.find(e => e.$validator === validatorName);
    if (error?.$params) {
      return error.$params;
    }
  }

  // 3. Vuelidate 2: $params may be flat (not nested under validator name)
  if (v?.$params && !v.$params[validatorName] && Object.keys(v.$params).length > 0) {
    return v.$params;
  }

  // 4. Vuelidate 1: $params nested under validator name
  if (v?.$params?.[validatorName]) {
    return v.$params[validatorName];
  }

  // 5. Look up static $params from the validator function in fieldRules.
  //    For helpers.forEach per-item proxies, the Vuelidate proxy doesn't
  //    expose $params or $silentErrors, but the original validator function
  //    (stored in fieldRules) has $params set explicitly.

  // 5a. Try $path (e.g. "applicants.0.PostalCode" → "PostalCode")
  const fieldName = v?.$path?.split('.')?.pop();
  if (fieldName && fieldRules[fieldName]?.[validatorName]?.$params) {
    return fieldRules[fieldName][validatorName].$params;
  }

  // 5b. Identify the field by checking which fieldRules validators exist on
  //     the proxy via the `in` operator (works with Vuelidate Proxy has-traps,
  //     unlike Object.keys which may not enumerate proxy properties).
  //     Pick the most specific match (most rule keys) to avoid false positives.
  if (v) {
    let bestParams = null;
    let bestKeyCount = 0;
    for (const [field, rules] of Object.entries(fieldRules)) {
      if (rules[validatorName]?.$params) {
        const ruleKeys = Object.keys(rules);
        if (ruleKeys.every(k => k in v) && ruleKeys.length > bestKeyCount) {
          bestParams = rules[validatorName].$params;
          bestKeyCount = ruleKeys.length;
        }
      }
    }
    if (bestParams) return bestParams;
  }

  return {};
}

export default {
  ssn: translate('Henkilötunnus on virheellinen.'),
  integer: translate('Vain kokonaisluvut ovat sallittuja.'),
  numeric: translate('Vain numerot ovat sallittuja.'),
  email: translate('Sähköposti on virheellinen.'),
  required: translate('Kenttä on pakollinen.'),
  sameAs: translate('Kentät eivät täsmää.'),
  noDigits: translate('Numerot eivät ole sallittuja.'),
  phone: translate('Vain numerot ovat sallittuja.'),
  noNumbersAllowed: translate('Syötäthän vain kirjaimia.'),
  maxLength: (v, errorEntry) => {
    const params = getParams(v, 'maxLength', errorEntry);
    const max = params.max;
    const length = String(v.$model || '').length;

    if (max != null) {
      return sprintf(translate('Kentän maksimipituus on %s merkkiä. (%s)'), max, length);
    }
    return translate('Kentän arvo on liian pitkä.');
  },
  minLength: (v, errorEntry) => {
    const params = getParams(v, 'minLength', errorEntry);
    const min = params.min;
    const length = String(v.$model || '').length;
    if (min != null) {
      return sprintf(translate('Kentän minimipituus on %s merkkiä. (%s)'), min, length);
    }
    return translate('Kentän arvo on liian lyhyt.');
  },
  maxValue: (v, errorEntry) => {
    const params = getParams(v, 'maxValue', errorEntry);
    if (params.max != null) {
      return sprintf(translate('Kentän maksimiarvo on %s.'), params.max);
    }
    return translate('Kentän arvo on liian suuri.');
  },
  minValue: (v, errorEntry) => {
    const params = getParams(v, 'minValue', errorEntry);
    if (params.min != null) {
      return sprintf(translate('Kentän minimiarvo on %s.'), params.min);
    }
    return translate('Kentän arvo on liian pieni.');
  },
  applicationUniqueSsn: translate('Henkilötunnus esiintyy hakemuksella useasti.'),
};

/**
 * Resolve a validation message.
 * @param {string|Function} message - Static string or function(v, errorEntry)
 * @param {Object} v - Vuelidate field validation proxy
 * @param {Object} [errorEntry] - Optional error entry from $silentErrors/$errors
 * @param {*} [modelValue] - Optional captured input value (for helpers.forEach
 *   per-item proxies where v.$model is unavailable)
 */
export function displayMessage(message, v, errorEntry, modelValue) {
  if (typeof message !== 'function') return message;

  // If $model is missing on the proxy but we have a captured value, create
  // a lightweight wrapper so the message function can read v.$model.
  const vResolved = (v && v.$model == null && modelValue != null)
    ? Object.create(v, { $model: { value: modelValue, enumerable: true } })
    : v;

  return message(vResolved, errorEntry);
}
